// Assets
import FontFamily from 'assets/font-family.json';

import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// Components
import {Select} from 'components/Controls';
import { ColorPicker } from 'components/ColorPicker';
// Utilities
import { isRequired } from 'utilities/validation';
// i18next
import { useTranslation } from 'react-i18next';

const ThemeForm:FC = () => {
  const { t } = useTranslation();

  const { control, formState: { errors }, watch } = useFormContext();

  const watchedPrimaryFont = watch('theme.primaryFont');

  return (
    <div className="w-full">
      <Controller
        control={control} name="theme.primaryFont"
        rules={{ required: isRequired }}
        render={({ field }) => (
          <Select
            {...field}
            options={FontFamily.families}
            label={t('labels.fonts')} id="primaryFont" name="primaryFont"
            required={true}
            error={Boolean(errors.theme?.primaryFont)}
            errorText={errors.theme?.primaryFont ? errors.theme.primaryFont.message : ''}
          />
        )}
      />
      <p className="text-white text-xl mb-4" style={{ fontFamily: `"${watchedPrimaryFont}", sans-serif` }}>
        {t('labels.howTextWillLook')}
      </p>
      <div className="flex flex-wrap">
        <div className="w-full sm:w-1/3">
          <div className="mr-4 mb-4">
            <Controller
              control={control} name="theme.primaryColor" rules={{ required: isRequired }}
              render={({ field: { value, onChange } }) => (
                <ColorPicker
                  label={t('labels.primaryColor')}
                  value={value}
                  onChange={onChange}
                  error={Boolean(errors.theme?.primaryColor)}
                  errorText={errors.theme?.primaryColor ? errors.theme.primaryColor.message : ''}
                  helperText=""
                />
              )}
            />
            <span className="text-white text-xs font-light">{t('pages.quizForm.primaryColorHint')}</span>
          </div>
        </div>
        <div className="w-full sm:w-1/3">
          <div className="mr-4 mb-4">
            <Controller
              control={control} name="theme.primaryFontColor" rules={{ required: isRequired }}
              render={({ field: { value, onChange } }) => (
                <ColorPicker
                  label={t('labels.primaryFontColor')}
                  value={value}
                  onChange={onChange}
                  error={Boolean(errors.theme?.primaryFontColor)}
                  errorText={errors.theme?.primaryFontColor ? errors.theme.primaryFontColor.message : ''}
                  helperText={t('pages.quizForm.primaryFontColorHint')}
                />
              )}
            />
          </div>
        </div>
        <div className="w-full sm:w-1/3">
          <div className="mr-4 mb-4">
            <Controller
              control={control} name="theme.progressBarColor" rules={{ required: isRequired }}
              render={({ field: { value, onChange } }) => (
                <ColorPicker
                  label={t('pages.quizForm.progressBarColor')}
                  value={value}
                  required={true}
                  error={Boolean(errors.theme?.progressBarColor)}
                  errorText={errors.theme?.progressBarColor ? errors.theme.progressBarColor.message : ''}
                  onChange={onChange}
                  helperText={t('pages.quizForm.progressBarColorHint')}
                />
              )}
            />
          </div>
        </div>
        <div className="w-full sm:w-1/3">
          <div className="mr-4 mb-4">
            <Controller
              control={control} name="theme.contentBackgroundColor" rules={{ required: isRequired }}
              render={({ field: { value, onChange } }) => (
                <ColorPicker
                  label={t('pages.quizForm.contentBackgroundColor')}
                  value={value}
                  required={true}
                  error={Boolean(errors.theme?.contentBackgroundColor)}
                  errorText={errors.theme?.contentBackgroundColor ? errors.theme.contentBackgroundColor.message : ''}
                  onChange={onChange}
                  helperText={t('pages.quizForm.contentBackgroundColorHint')}
                />
              )}
            />
          </div>
        </div>
      </div>
      <h2 className="text-xl font-bold text-white mb-2">
        {t('labels.actionButtonsColor')}
      </h2>
      <div className="flex flex-wrap gap-4">
        <div className="mb-4">
          <Controller
            control={control} name="theme.primaryButtonGradientStart" rules={{ required: isRequired }}
            render={({ field: { value, onChange } }) => (
              <ColorPicker
                label={t('labels.startColor')}
                value={value}
                onChange={onChange}
                error={Boolean(errors.theme?.primaryButtonGradientStart)}
                errorText={errors.theme?.primaryButtonGradientStart ? errors.theme.primaryButtonGradientStart.message : ''}
                helperText=""
              />
            )}
          />
        </div>
        <div className="mb-4">
          <Controller
            control={control} name="theme.primaryButtonGradientEnd" rules={{ required: isRequired }}
            render={({ field: { value, onChange } }) => (
              <ColorPicker
                label={t('labels.endColor')}
                value={value}
                onChange={onChange}
                error={Boolean(errors.theme?.primaryButtonGradientEnd)}
                errorText={errors.theme?.primaryButtonGradientEnd ? errors.theme.primaryButtonGradientEnd.message : ''}
              />
            )}
          />
        </div>
        <div className="mb-4">
          <Controller
            control={control} name="theme.primaryButtonFontColor" rules={{ required: isRequired }}
            render={({ field: { value, onChange } }) => (
              <ColorPicker
                label={t('labels.fontColor')}
                value={value}
                onChange={onChange}
                error={Boolean(errors.theme?.primaryButtonFontColor)}
                errorText={errors.theme?.primaryButtonFontColor ? errors.theme.primaryButtonFontColor.message : ''}
              />
            )}
          />
        </div>
      </div>
      <span className="block text-white text-xs font-light mb-4">{t('form.helperText.buttonsGradientColorsHint')}</span>

      <h2 className="text-xl font-bold text-white mb-2">
        {t('pages.quizForm.selectedAnswerButtonColor')}
      </h2>
      <div className="flex flex-wrap gap-4">
        <div className="mb-4">
          <Controller
            control={control} name="theme.selectedAnswerButtonGradientStart" rules={{ required: isRequired }}
            render={({ field: { value, onChange } }) => (
              <ColorPicker
                label={t('labels.startColor')}
                value={value}
                onChange={onChange}
                error={Boolean(errors.theme?.selectedAnswerButtonGradientStart)}
                errorText={errors.theme?.selectedAnswerButtonGradientStart ? errors.theme.selectedAnswerButtonGradientStart.message : ''}
                helperText=""
              />
            )}
          />
        </div>
        <div className="mb-4">
          <Controller
            control={control} name="theme.selectedAnswerButtonGradientEnd" rules={{ required: isRequired }}
            render={({ field: { value, onChange } }) => (
              <ColorPicker
                label={t('labels.endColor')}
                value={value}
                onChange={onChange}
                error={Boolean(errors.theme?.selectedAnswerButtonGradientEnd)}
                errorText={errors.theme?.selectedAnswerButtonGradientEnd ? errors.theme.selectedAnswerButtonGradientEnd.message : ''}
              />
            )}
          />
        </div>
        <div className="mb-4">
          <Controller
            control={control} name="theme.selectedAnswerButtonFontColor" rules={{ required: isRequired }}
            render={({ field: { value, onChange } }) => (
              <ColorPicker
                label={t('labels.fontColor')}
                value={value}
                onChange={onChange}
                error={Boolean(errors.theme?.selectedAnswerButtonFontColor)}
                errorText={errors.theme?.selectedAnswerButtonFontColor ? errors.theme.selectedAnswerButtonFontColor.message : ''}
              />
            )}
          />
        </div>
      </div>
    </div>
  )
}

export default ThemeForm;
